import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import { InlineError } from "./Feedback";

export interface MultiSelectOption {
  disabled?: boolean;
  label: string;
  value: string;
}

export interface MultiSelectProps {
  /** Uncontrolled initial selection. Ignored when `value` is provided. */
  defaultValue?: string[];
  description?: ReactNode;
  disabled?: boolean;
  /** Shown inside the popover when no options are available. */
  emptyLabel?: string;
  error?: ReactNode;
  /** A `HelpTip` rendered beside the label (UI-LAW §4d). */
  help?: ReactNode;
  id?: string;
  label: string;
  name?: string;
  onChange?: (value: string[]) => void;
  options: MultiSelectOption[];
  placeholder?: string;
  /** Controlled selection. When provided the component is controlled. */
  value?: string[];
}

function ChevronIcon() {
  return (
    <svg
      aria-hidden="true"
      className="marker-multi-select__icon"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function RemoveIcon() {
  return (
    <svg
      aria-hidden="true"
      className="marker-multi-select__icon"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      aria-hidden="true"
      className="marker-multi-select__check"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="m5 12 5 5 9-11" />
    </svg>
  );
}

function nextEnabledIndex(
  options: MultiSelectOption[],
  from: number,
  step: 1 | -1,
): number {
  const count = options.length;
  if (count === 0) {
    return -1;
  }
  for (let offset = 1; offset <= count; offset += 1) {
    const index = (from + step * offset + count * offset) % count;
    if (options[index]?.disabled !== true) {
      return index;
    }
  }
  return -1;
}

function firstEnabledIndex(options: MultiSelectOption[]): number {
  return options.findIndex((option) => option.disabled !== true);
}

export function MultiSelect({
  defaultValue,
  description,
  disabled = false,
  emptyLabel = "No options.",
  error,
  help,
  id,
  label,
  name,
  onChange,
  options,
  placeholder = "Select…",
  value,
}: MultiSelectProps) {
  const generatedId = useId();
  const baseId = id ?? name ?? generatedId;
  const labelId = `${baseId}-label`;
  const triggerId = `${baseId}-trigger`;
  const listboxId = `${baseId}-listbox`;
  const descriptionId = description ? `${baseId}-description` : undefined;
  const errorId = error ? `${baseId}-error` : undefined;

  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState<string[]>(
    defaultValue ?? [],
  );
  const selected = isControlled ? value : internalValue;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const selectedOptions = useMemo(
    () => options.filter((option) => selectedSet.has(option.value)),
    [options, selectedSet],
  );

  const commit = useCallback(
    (nextValue: string[]) => {
      if (!isControlled) {
        setInternalValue(nextValue);
      }
      onChange?.(nextValue);
    },
    [isControlled, onChange],
  );

  const toggleValue = useCallback(
    (optionValue: string) => {
      const nextValue = selectedSet.has(optionValue)
        ? selected.filter((entry) => entry !== optionValue)
        : [...selected, optionValue];
      commit(nextValue);
    },
    [commit, selected, selectedSet],
  );

  const removeValue = useCallback(
    (optionValue: string) => {
      commit(selected.filter((entry) => entry !== optionValue));
    },
    [commit, selected],
  );

  const openPopover = useCallback((focusIndex: number) => {
    setOpen(true);
    setActiveIndex(focusIndex);
  }, []);

  const closePopover = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  // Close on outside pointer press.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      const node = containerRef.current;
      if (
        node &&
        event.target instanceof Node &&
        !node.contains(event.target)
      ) {
        closePopover();
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [closePopover, open]);

  // Keep the active option within the visible scroll area.
  useEffect(() => {
    if (!open || activeIndex < 0) {
      return;
    }
    const listbox = listboxRef.current;
    const activeEl = listbox?.querySelector<HTMLElement>(
      '[data-active="true"]',
    );
    if (activeEl && typeof activeEl.scrollIntoView === "function") {
      activeEl.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex, open]);

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        if (!open) {
          const start =
            activeIndex >= 0 ? activeIndex : firstEnabledIndex(options);
          openPopover(start);
          return;
        }
        setActiveIndex((current) => nextEnabledIndex(options, current, 1));
        return;
      }
      case "ArrowUp": {
        event.preventDefault();
        if (!open) {
          openPopover(nextEnabledIndex(options, 0, -1));
          return;
        }
        setActiveIndex((current) => nextEnabledIndex(options, current, -1));
        return;
      }
      case "Home": {
        if (open) {
          event.preventDefault();
          setActiveIndex(firstEnabledIndex(options));
        }
        return;
      }
      case "End": {
        if (open) {
          event.preventDefault();
          setActiveIndex(nextEnabledIndex(options, 0, -1));
        }
        return;
      }
      case "Enter":
      case " ": {
        event.preventDefault();
        if (!open) {
          openPopover(
            activeIndex >= 0 ? activeIndex : firstEnabledIndex(options),
          );
          return;
        }
        const activeOption = options[activeIndex];
        if (activeOption && activeOption.disabled !== true) {
          toggleValue(activeOption.value);
        }
        return;
      }
      case "Escape": {
        if (open) {
          event.preventDefault();
          closePopover();
        }
        return;
      }
      case "Backspace": {
        const last = selected[selected.length - 1];
        if (last !== undefined) {
          event.preventDefault();
          removeValue(last);
        }
        return;
      }
      default:
    }
  };

  const activeDescendant =
    open && activeIndex >= 0 && options[activeIndex]
      ? `${listboxId}-option-${String(activeIndex)}`
      : undefined;

  const controlClasses = ["marker-multi-select__control"];
  if (open) {
    controlClasses.push("marker-multi-select__control--open");
  }
  if (disabled) {
    controlClasses.push("marker-multi-select__control--disabled");
  }

  return (
    <div className="marker-field marker-multi-select">
      <span className="marker-field__label-row">
        <span className="marker-field__label" id={labelId}>
          {label}
        </span>
        {help}
      </span>
      <div className="marker-multi-select__anchor" ref={containerRef}>
        <div className={controlClasses.join(" ")}>
          {selectedOptions.map((option) => (
            <span className="marker-multi-select__chip" key={option.value}>
              <span className="marker-multi-select__chip-label">
                {option.label}
              </span>
              <button
                aria-label={`Remove ${option.label}`}
                className="marker-multi-select__chip-remove"
                disabled={disabled}
                onClick={() => {
                  removeValue(option.value);
                  triggerRef.current?.focus();
                }}
                tabIndex={-1}
                type="button"
              >
                <RemoveIcon />
              </button>
            </span>
          ))}
          <div
            aria-activedescendant={activeDescendant}
            aria-controls={listboxId}
            aria-describedby={
              [descriptionId, errorId].filter(Boolean).join(" ") || undefined
            }
            aria-disabled={disabled || undefined}
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-invalid={error ? true : undefined}
            aria-labelledby={`${labelId} ${triggerId}`}
            className="marker-multi-select__trigger"
            id={triggerId}
            onClick={() => {
              if (disabled) {
                return;
              }
              if (open) {
                closePopover();
              } else {
                openPopover(
                  activeIndex >= 0 ? activeIndex : firstEnabledIndex(options),
                );
              }
            }}
            onKeyDown={onTriggerKeyDown}
            ref={triggerRef}
            role="combobox"
            tabIndex={disabled ? -1 : 0}
          >
            {selectedOptions.length === 0 ? (
              <span className="marker-multi-select__placeholder">
                {placeholder}
              </span>
            ) : (
              <span className="marker-multi-select__count">
                {selectedOptions.length} selected
              </span>
            )}
            <ChevronIcon />
          </div>
        </div>
        {open ? (
          <div className="marker-scope-popover marker-multi-select__popover">
            <div
              aria-labelledby={labelId}
              aria-multiselectable="true"
              className="marker-multi-select__listbox"
              id={listboxId}
              ref={listboxRef}
              role="listbox"
            >
              {options.length === 0 ? (
                <div className="marker-multi-select__empty">{emptyLabel}</div>
              ) : (
                options.map((option, index) => {
                  const isSelected = selectedSet.has(option.value);
                  const isActive = index === activeIndex;
                  const optionId = `${listboxId}-option-${String(index)}`;
                  const optionClasses = ["marker-multi-select__option"];
                  if (isActive) {
                    optionClasses.push("marker-multi-select__option--active");
                  }
                  return (
                    <button
                      aria-selected={isSelected}
                      className={optionClasses.join(" ")}
                      data-active={isActive || undefined}
                      disabled={option.disabled}
                      id={optionId}
                      key={option.value}
                      onClick={() => {
                        toggleValue(option.value);
                        setActiveIndex(index);
                        triggerRef.current?.focus();
                      }}
                      onMouseEnter={() => {
                        if (option.disabled !== true) {
                          setActiveIndex(index);
                        }
                      }}
                      role="option"
                      tabIndex={-1}
                      type="button"
                    >
                      <span className="marker-multi-select__option-label">
                        {option.label}
                      </span>
                      <span className="marker-multi-select__option-check">
                        {isSelected ? <CheckIcon /> : null}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        ) : null}
      </div>
      {description ? (
        <span className="marker-field__description" id={descriptionId}>
          {description}
        </span>
      ) : null}
      {error ? <InlineError id={errorId}>{error}</InlineError> : null}
    </div>
  );
}
